import React, { useEffect } from 'react'
import { Text, Stack, Checkbox } from '@chakra-ui/react'
import { LabeledInput, LabeledSelect } from '@apex/component-library'
import { FormikErrors, FormikTouched } from 'formik'
import { DistributionReasonTypes } from '../../containers/CheckTransfers/Types'
import { DistributionReasonInfo, TaxWithholdingTypes } from '@Types'
import { TaxWithholdingFields } from './TaxWithholdingFields'
import { useWaiverStates } from '../../api'
export interface IraDistributionFormikValues {
  distributionReason?: DistributionReasonTypes
  federalTaxWithholdingType?: TaxWithholdingTypes
  federalTaxWithholdingAmountDisplay?: string
  federalTaxWithholdingAmountRaw?: number
  stateTaxWithholdingType?: TaxWithholdingTypes
  stateTaxWithholdingAmountDisplay?: string
  stateTaxWithholdingAmountRaw?: number
  receivingInstitutionName?: string
  optOutFormProvided?: boolean
}
export interface IraDistributionFormProps {
  formikValues: IraDistributionFormikValues
  formikBlur: (e: React.FocusEvent<any>) => void
  formikTouched: FormikTouched<IraDistributionFormikValues>
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  setFieldTouched: (field: string, value: boolean, shouldValidate?: boolean | undefined) => void
  formikSetValues: (values: React.SetStateAction<any>, shouldValidate?: boolean | undefined) => void
  setTouched: (fields: object, validate: boolean) => void
  errors: FormikErrors<IraDistributionFormikValues>
  distributionReasonOptions?: DistributionReasonInfo[]
  selectedReason?: DistributionReasonInfo | null
  handleSelectReason: (reason: DistributionReasonInfo | null) => void
  receivingInstitutionName?: string
}

const IraDistributionForm = ({
  formikValues,
  distributionReasonOptions,
  selectedReason,
  handleSelectReason,
  formikBlur,
  formikTouched,
  setFieldValue,
  setFieldTouched,
  setTouched,
  errors,
  formikSetValues,
  receivingInstitutionName
}: IraDistributionFormProps) => {
  const { distributionReason, optOutFormProvided } = formikValues

  const handleDistributionReasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target
    setFieldValue('distributionReason', value)
    const selected = distributionReasonOptions?.find((option) => option.reason === value)
    selected ? handleSelectReason(selected) : handleSelectReason(null)
  }
  const displayOptOutForm = useWaiverStates()

  useEffect(() => {
    formikSetValues({
      ...formikValues,
      federalTaxWithholdingType: '',
      federalTaxWithholdingAmountRaw: 0,
      federalTaxWithholdingAmountDisplay: '',
      stateTaxWithholdingType: '',
      stateTaxWithholdingAmountRaw: 0,
      stateTaxWithholdingAmountDisplay: '',
      optOutFormProvided: false
    })
    // formikSetValues is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReason])

  return (
    <>
      <Text as='h6' className='test-id-distribution-header'>
        IRA Distributions require the following information:
      </Text>
      <Stack spacing={4}>
        <LabeledSelect
          label='Distribution Reason'
          aria-label='Distribution Reason'
          isRequired
          name='distributionReason'
          onBlur={formikBlur}
          value={distributionReason}
          onChange={(e) => {
            handleDistributionReasonChange(e)
            setFieldValue('receivingInstitutionName', '')
            setFieldTouched('receivingInstitutionName', false)
          }}
          isInvalid={formikTouched.distributionReason && !!errors?.distributionReason}
          errorMessage={errors?.distributionReason}
        >
          <option></option>
          {distributionReasonOptions?.map((option, index) => (
            <option key={`${index}-${option.reason}`} value={option.reason}>
              {option.reason}
            </option>
          ))}
        </LabeledSelect>

        {distributionReason === 'TRANSFER' && (
          <LabeledInput
            label='Receiving Institution Name'
            isRequired
            name='receivingInstitutionName'
            onBlur={formikBlur}
            value={receivingInstitutionName}
            onChange={(e) => {
              setFieldValue('receivingInstitutionName', e.target.value)
            }}
            isInvalid={formikTouched.receivingInstitutionName && !!errors?.receivingInstitutionName}
            errorMessage={errors?.receivingInstitutionName}
          />
        )}
        <TaxWithholdingFields
          formikSetValues={formikSetValues}
          formikValues={formikValues}
          formikBlur={formikBlur}
          formikTouched={formikTouched}
          setFieldValue={setFieldValue}
          setTouched={setTouched}
          errors={errors}
          federalTaxWithholdingEnabled={selectedReason?.withholdingEnabled}
          stateTaxWithholdingEnabled={selectedReason?.stateWithholdingEnabled}
        />
        {displayOptOutForm && (
          <Checkbox
            name='optOutFormProvided'
            display='inline'
            data-testid='optOutFormProvided'
            isChecked={optOutFormProvided}
            onChange={(e) => {
              setFieldValue('optOutFormProvided', e.target.checked)
            }}
          >
            {`Opt Out Form Provided`}
          </Checkbox>
        )}
      </Stack>
    </>
  )
}

export default IraDistributionForm
